import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    ViewChild
} from '@angular/core';
import { PlatformService } from '@core/services/platform.service';
import { ConsentType } from '@core/services/cookie-consent/cookie-consent.config';
import { ParseStylePipe } from './parse-style.pipe';
import { SafePipe } from '@shared/safe/safe.pipe';
import { ThirdPartyLoaderComponent } from '../third-party-loader/third-party-loader.component';
import { LoadingDotsComponent } from '../../../@shared/loading-dots/loading-dots.component';
import { NgIf, NgFor, NgStyle } from '@angular/common';

type MessageData = {
    action: 'resize' | 'campaign-ready' | 'request-url';
    campaignId: number;
    data: {
        width: number;
        height: number;
    };
    type: string | 'leadfamly-campaign';
    uri: string;
};

@Component({
    selector: 'app-iframe-loader',
    templateUrl: './iframe-loader.component.html',
    styleUrls: ['./iframe-loader.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, LoadingDotsComponent, NgFor, ThirdPartyLoaderComponent, NgStyle, SafePipe, ParseStylePipe]
})
export class IframeLoaderComponent implements AfterViewInit, OnDestroy {
    public isFetching: boolean = true;

    @Input() public iFrameStyle?: string;
    @Input() public id: string = `iframe-${Math.random().toString(36).substring(2, 9)}`;

    @Input() public title: string;
    @Input() public src: string;
    @Input() public extraScripts: string[] = [];
    @Input() public usePostMessageEvent: boolean = false;
    @Input() public consent: ConsentType;

    @ViewChild('iframeEl')
    iframeEl: ElementRef<HTMLIFrameElement>;

    constructor(
        private readonly ngZone: NgZone,
        private readonly platformService: PlatformService,
        private readonly cd: ChangeDetectorRef
    ) {}

    ngAfterViewInit(): void {
        if (this.platformService.isBrowser && this.usePostMessageEvent) {
            window.addEventListener('message', this.onMessage.bind(this));
        }
    }

    ngOnDestroy(): void {
        if (this.platformService.isBrowser && this.usePostMessageEvent) {
            window.removeEventListener('message', this.onMessage.bind(this));
        }
    }

    /**
     * this method is used to receive messages from iframe, the main purpose of this method is to resize the iframe automatically based on the iframe content
     * it's very specific for the ladfamly iframe, if we need more cases they need to be added here
     * @param event
     * @private
     */
    private onMessage(event: MessageEvent) {
        const { data: eventData } = event;

        try {
            const messageData = typeof eventData === 'string' ? JSON.parse(eventData) : eventData;

            if (messageData) {
                this.ngZone.runOutsideAngular(() => {
                    const { action, type, data } = messageData as MessageData;

                    if (type !== 'leadfamly-campaign') {
                        return;
                    }

                    console.warn(`${type}: ${action} - ${JSON.stringify(data)}`);
                    if (action === 'resize') {
                        const { height } = data;
                        this.iframeEl.nativeElement.style.height = `${height}px`;
                    }

                    if (action === 'campaign-ready') {
                        this.ngZone.runGuarded(() => {
                            this.isFetching = false;
                            this.cd.detectChanges();
                        });
                    }
                });
            }
        } catch (error) {
            console.error(error);
        }
    }
}
